import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { useUser } from '../../context/UserContext';
import { track } from '../../utils/analytics';
import '../../styles/onboarding-widget.css';
import '../../styles/welcome.css';

const EXCLUDED_PATHS = [
  '/sign-in',
  '/sign-up',
  '/restore',
  '/banned',
  '/confirm',
  '/welcome',
  '/admin',
  '/payment',
  '/settings/plan/purchase',
];

const DISMISSED_KEY = 'onboarding_widget_dismissed';
const COLLAPSED_KEY = 'onboarding_widget_collapsed';
const INTENT_STORAGE_KEY = 'onboarding_intent';

interface MeResponse {
  has_built_resume: boolean;
  has_ai_cv_review: boolean;
  has_generated_cover_letter: boolean;
  has_tracked_job_applications: boolean;
}

interface Kanban {
  uid: string;
}

interface CV {
  uid: string;
  title: string;
  updated_at?: string | null;
  created_at?: string | null;
}

type IntentChoice = 'has-cv' | 'no-cv';

interface StepConfig {
  id: 'build-resume' | 'ai-review' | 'first-job' | 'ai-tools';
  index: number;
  title: string;
  doneKey: keyof MeResponse;
}

const STEPS: StepConfig[] = [
  { id: 'build-resume', index: 1, title: 'Build your resume', doneKey: 'has_built_resume' },
  { id: 'first-job', index: 2, title: 'Save your first job', doneKey: 'has_tracked_job_applications' },
  { id: 'ai-review', index: 3, title: 'Get AI feedback', doneKey: 'has_ai_cv_review' },
  { id: 'ai-tools', index: 4, title: 'Try AI tools', doneKey: 'has_generated_cover_letter' },
];

const OnboardingChecklistWidget: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useUser();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [boardUid, setBoardUid] = useState<string | null>(null);
  const [cvs, setCvs] = useState<CV[]>([]);
  const [cvPickerOpen, setCvPickerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) return stored === 'true';
    // The expanded card covers roughly half a phone screen; start it collapsed
    // on mobile and let the user open it. A stored choice always wins.
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  });
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISSED_KEY) === 'true',
  );

  const isExcluded = useMemo(() => {
    const path = location.pathname;
    return EXCLUDED_PATHS.some(p => path.startsWith(p));
  }, [location.pathname]);

  const fetchData = useCallback(async () => {
    if (!userProfile) return;
    try {
      const [meRes, kanbansRes, cvsRes] = await Promise.allSettled([
        api.get<MeResponse>(API_ENDPOINTS.me),
        api.get<Kanban[] | { kanbans: Kanban[] }>(API_ENDPOINTS.kanbans.list),
        api.get<CV[]>(API_ENDPOINTS.cvs.list),
      ]);
      if (meRes.status === 'fulfilled' && meRes.value) {
        setMe(meRes.value);
      }
      if (kanbansRes.status === 'fulfilled' && kanbansRes.value) {
        const raw = kanbansRes.value;
        const list: Kanban[] = Array.isArray(raw)
          ? raw
          : (raw as { kanbans?: Kanban[] }).kanbans ?? [];
        if (list.length > 0) setBoardUid(list[0].uid);
      }
      if (cvsRes.status === 'fulfilled' && Array.isArray(cvsRes.value)) {
        setCvs(cvsRes.value);
      }
    } catch {
    }
  }, [userProfile]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (dismissed || isExcluded) return;
    fetchData();
  }, [dismissed, isExcluded, fetchData]);

  const doneSteps = useMemo(() => {
    if (!me) return 0;
    return STEPS.filter(s => me[s.doneKey]).length;
  }, [me]);

  const totalSteps = STEPS.length;
  const allDone = doneSteps >= totalSteps;

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      track('onboarding_widget_toggled', { collapsed: next });
      return next;
    });
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
    track('onboarding_widget_dismissed', { progress: doneSteps });
  };

  const handleStep = (step: StepConfig) => {
    if (me && me[step.doneKey]) return;
    track('onboarding_widget_step_clicked', { step: step.id });

    const intent = localStorage.getItem(INTENT_STORAGE_KEY) as IntentChoice | null;

    switch (step.id) {
      case 'build-resume': {
        const target =
          intent === 'has-cv'
            ? '/resume-builder/new-base-resume?source=upload'
            : intent === 'no-cv'
              ? '/resume-builder/new-base-resume?blank=1'
              : '/resume-builder/new-base-resume';
        navigate(target);
        break;
      }
      case 'ai-review':
        if (cvs.length === 1) {
          navigate(`/resume-builder/editor/${cvs[0].uid}?tab=ai-reviewer`);
        } else if (cvs.length > 1) {
          setCvPickerOpen(true);
        } else {
          navigate('/resume-builder');
        }
        break;
      case 'first-job':
        navigate(boardUid ? `/track/board/${boardUid}?onboarding=new-job` : '/dashboard');
        break;
      case 'ai-tools':
        navigate('/ai-tools');
        break;
    }
  };

  if (dismissed || isExcluded || !userProfile || !me || allDone) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="ocw-pill"
        onClick={toggleCollapsed}
        aria-label="Open onboarding checklist"
      >
        <span className="ocw-pill__icon">✓</span>
        <span className="ocw-pill__text">
          Getting started {doneSteps}/{totalSteps}
        </span>
      </button>
    );
  }

  const handleCvPicked = (cvUid: string) => {
    setCvPickerOpen(false);
    track('onboarding_widget_cv_picked_for_review', { cv_uid: cvUid });
    navigate(`/resume-builder/editor/${cvUid}?tab=ai-reviewer`);
  };

  return (
    <>
    <div className="ocw-card" role="dialog" aria-label="Onboarding checklist">
      <div className="ocw-card__header">
        <div className="ocw-card__title">Getting started</div>
        <div className="ocw-card__actions">
          <button
            type="button"
            className="ocw-card__icon-btn"
            onClick={toggleCollapsed}
            aria-label="Minimize"
            title="Minimize"
          >
            –
          </button>
          <button
            type="button"
            className="ocw-card__icon-btn"
            onClick={handleDismiss}
            aria-label="Dismiss"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      </div>

      <div className="ocw-card__progress">
        <div className="ocw-card__progress-track">
          <div
            className="ocw-card__progress-fill"
            style={{ width: `${(doneSteps / totalSteps) * 100}%` }}
          />
        </div>
        <div className="ocw-card__progress-label">
          {doneSteps} of {totalSteps} done
        </div>
      </div>

      <ul className="ocw-card__list">
        {STEPS.map(step => {
          const isDone = Boolean(me[step.doneKey]);
          return (
            <li key={step.id} className={`ocw-step ${isDone ? 'ocw-step--done' : ''}`}>
              <button
                type="button"
                className="ocw-step__btn"
                onClick={() => handleStep(step)}
                disabled={isDone}
              >
                <span className="ocw-step__check">{isDone ? '✓' : ''}</span>
                <span className="ocw-step__title">{step.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>

    {cvPickerOpen && (
      <div
        className="welcome-picker__overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ocw-picker-title"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setCvPickerOpen(false);
        }}
      >
        <div className="welcome-picker">
          <div className="welcome-picker__header">
            <h2 id="ocw-picker-title" className="welcome-picker__title">
              Choose a resume to review
            </h2>
            <button
              type="button"
              className="welcome-picker__close"
              onClick={() => setCvPickerOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="welcome-picker__hint">
            Pick the resume you want AI to analyze. We'll open it and switch to the AI Reviewer tab.
          </p>
          <ul className="welcome-picker__list">
            {cvs.map(cv => (
              <li key={cv.uid}>
                <button
                  type="button"
                  className="welcome-picker__item"
                  onClick={() => handleCvPicked(cv.uid)}
                >
                  <span className="welcome-picker__item-title">{cv.title || 'Untitled resume'}</span>
                  {(cv.updated_at || cv.created_at) && (
                    <span className="welcome-picker__item-date">
                      Updated {new Date(cv.updated_at || cv.created_at || '').toLocaleDateString()}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}
    </>
  );
};

export default OnboardingChecklistWidget;
